import React, { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import type { ThgAnnualSummary, ThgStationRow } from "../../../types/thgAnnualSummary";
import { formatEur, formatKwh, freeEnergyKwh, ownerState } from "../utils/thgFormat";
import { COCHARGE_REVENUE_TEXTS } from "../../StationPerformance/StationPerformance.consts";
import { COPY, THG_TABLE_ROWS } from "../ThgAnnualSummary.consts";
import { hasPayoutData } from "../utils/thgPayout";
import { isInternalRow, isUnpaidInternal } from "../utils/thgInternal";
import { filterThgStations } from "../utils/thgSearch";
import PayoutStatusCell from "./PayoutStatusCell";
import PayoutActionsCell, { PayoutRowActions } from "./PayoutActionsCell";
import "./ThgStationTable.css";

interface ThgStationTableProps {
  summary: ThgAnnualSummary;
  /** CLR-283 credit-note actions. The payout columns appear only when the year carries payout data too. */
  payoutActions?: PayoutRowActions | null;
}

const num = "text-right white-space-nowrap";

const ownerName = (row: ThgStationRow) => {
  const state = ownerState(row);
  if (state === "NONE") return <span className="font-semibold">{COPY.noOwner}</span>;
  if (state === "RECORD_MISSING") return <span className="font-semibold">{COPY.ownerRecordMissing}</span>;
  return row.owner?.name;
};

/**
 * CLR-296: an internal owner's station is marked next to the owner. "Not paid" only when nothing was
 * issued; an internal row with an active credit note is a plain "Internal".
 */
const nameBody = (row: ThgStationRow) =>
  isInternalRow(row) ? (
    <span className="flex align-items-center gap-2">
      {ownerName(row)}
      <Tag
        value={isUnpaidInternal(row) ? COCHARGE_REVENUE_TEXTS.internalBadge : COCHARGE_REVENUE_TEXTS.internalPlain}
        className="thg-tag-neutral"
      />
    </span>
  ) : (
    ownerName(row)
  );

/**
 * One row per station, in the backend's order (by station ID).
 *
 * Rows nobody can be paid for are highlighted, because a payout list that hides them looks complete when
 * it is not. The footer is the backend's totals, never a sum of the rows: the backend rounds each row
 * and owns the rounding rule. The search (CLR-288) filters rows only: the footer stays the whole year's
 * totals, and the exports stay the whole year too.
 */
const ThgStationTable: React.FC<ThgStationTableProps> = ({ summary, payoutActions = null }) => {
  const { totals } = summary;
  const showPayouts = hasPayoutData(summary);
  // Controlled, so reloading the year after a credit-note action keeps the operator on their page.
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(THG_TABLE_ROWS);
  const [query, setQuery] = useState("");
  const searching = query.trim() !== "";
  const stations = useMemo(() => filterThgStations(summary.stations, query), [summary.stations, query]);
  const footer = (
    <ColumnGroup>
      <Row>
        <Column footer={searching ? COPY.totalAllStations : "Total"} colSpan={4} />
        <Column footer={formatKwh(totals.totalEnergyKwh)} footerClassName={num} />
        <Column footer={formatKwh(totals.paidEnergyKwh)} footerClassName={num} />
        <Column
          footer={formatKwh(freeEnergyKwh(totals.totalEnergyKwh, totals.paidEnergyKwh))}
          footerClassName={num}
        />
        <Column footer={formatEur(totals.payoutEur)} footerClassName={num} />
        {showPayouts && <Column footer="" colSpan={payoutActions ? 2 : 1} />}
      </Row>
    </ColumnGroup>
  );

  return (
    <div className="thg-station-table">
      {summary.stations.length > 0 && (
        <div className="flex flex-column gap-2 sm:flex-row sm:align-items-center mb-2">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setFirst(0); // a new search starts on page 1
              }}
              placeholder={COPY.searchPlaceholder}
              aria-label={COPY.searchPlaceholder}
              className="w-full sm:w-24rem"
              data-testid="thg-search"
            />
          </IconField>
          {searching && (
            <span className="text-sm text-color-secondary" data-testid="thg-search-count">
              {COPY.searchMatches(stations.length, summary.stations.length)}
            </span>
          )}
        </div>
      )}
      <DataTable
        value={stations}
        dataKey="stationId"
        paginator={stations.length > THG_TABLE_ROWS}
        first={first}
        rows={rows}
        onPage={(e) => {
          setFirst(e.first);
          setRows(e.rows);
        }}
        rowsPerPageOptions={[25, 50, 100]}
        footerColumnGroup={summary.stations.length ? footer : undefined}
        rowClassName={(row: ThgStationRow) => ({ "thg-unpayable": ownerState(row) !== "OK" })}
        emptyMessage={searching ? COPY.noSearchMatch(query) : COPY.emptyYear(summary.year)}
        size="small"
        stripedRows
      >
        <Column header="Name" body={nameBody} className="white-space-nowrap" />
        <Column
          header="E-Mail"
          body={(row: ThgStationRow) => row.owner?.email ?? ""}
          className="white-space-nowrap"
        />
        <Column header="Station" field="stationId" className="white-space-nowrap" />
        <Column
          header="First transaction"
          body={(row: ThgStationRow) => row.firstTransactionYear ?? ""}
          className="text-right"
        />
        <Column
          header="Total kWh"
          body={(row: ThgStationRow) => formatKwh(row.totalEnergyKwh)}
          className={num}
          headerClassName="justify-content-end"
        />
        <Column
          header="kWh excl. free charging"
          body={(row: ThgStationRow) => formatKwh(row.paidEnergyKwh)}
          className={`${num} font-semibold`}
          headerClassName="justify-content-end"
        />
        <Column
          header="Free charging kWh"
          body={(row: ThgStationRow) => formatKwh(freeEnergyKwh(row.totalEnergyKwh, row.paidEnergyKwh))}
          className={num}
          headerClassName="justify-content-end"
        />
        <Column
          header="Payout"
          body={(row: ThgStationRow) => formatEur(row.payoutEur)}
          className={`${num} font-semibold`}
          headerClassName="justify-content-end"
        />
        {showPayouts && (
          <Column header="Payout status" body={(row: ThgStationRow) => <PayoutStatusCell row={row} />} />
        )}
        {showPayouts && payoutActions && (
          <Column
            header="Credit note"
            body={(row: ThgStationRow) => <PayoutActionsCell row={row} {...payoutActions} />}
          />
        )}
      </DataTable>
    </div>
  );
};

export default ThgStationTable;
